import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, timer } from 'rxjs';
import { expand, map, switchMap } from 'rxjs/operators';
import { BYPASS_AUTH } from 'src/app/_services/interceptors/http-config.interceptor';
import { SocialListeningSentimentType } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/sentiment/sentiment-chip.component';
import {
	FindAccountListEntry,
	FindAccountListJob,
	FindCityLookupItem,
	FindRegion,
	FindCreateSearchRequest,
	FindExportView,
	FindFilters,
	FindNewMentionsCount,
	FindPlatformCounts,
	FindReadMentionsRequest,
	FindReadDates,
	FindRunningList,
	FindRunOptions,
	FindSearch,
	FindSearchAccountList,
	FindHistoryPage,
	FindHistoryRequest,
	FindSearchList,
	FindStopRequest,
	FindSuggestions,
	FindUnknownLocationCount,
	FindUpdateSearchRequest
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-find-mentions.model';
import {
	normalizeSocialListeningPlatform,
	SocialListeningMention,
	SocialListeningMentionApi,
	SocialListeningMentionsResponse,
	SocialListeningMentionsResponseApi
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { smiUrl } from 'components';

/** Status polls while the question is being read (design D6). */
export const FIND_INTERPRETING_POLL_MS = 2000;
/** Status polls while collection runs: the progress card and the running list read the same run, so both poll at 5 s. */
export const FIND_COLLECTING_POLL_MS = 5000;
/** Account-list parse job polls. */
export const FIND_ACCOUNT_LIST_POLL_MS = 1500;
/** How many catalogue cities one lookup asks for. */
export const FIND_CITY_LOOKUP_LIMIT = 20;

export interface IFindMentionsService {
	createSearch(request: FindCreateSearchRequest): Observable<FindSearch>;
	listSearches(): Observable<FindSearchList>;
	/** One page of the brand's whole search history, narrowed by the favorites and running filters. */
	listHistory(request: FindHistoryRequest): Observable<FindHistoryPage>;
	getSearch(searchId: string): Observable<FindSearch>;
	watchSearch(searchId: string): Observable<FindSearch>;
	/** The search's whole account list — a search response carries only its count. */
	getSearchAccountList(searchId: string): Observable<FindAccountListEntry[]>;
	updateSearch(
		searchId: string,
		request: FindUpdateSearchRequest
	): Observable<FindSearch>;
	runSearch(
		searchId: string,
		options?: FindRunOptions
	): Observable<FindSearch>;
	/** Soft stop of a running search; `pause` marks it paused so it can be resumed. Answers the search as it now stands. */
	stopSearch(
		searchId: string,
		request: FindStopRequest
	): Observable<FindSearch>;
	/** Every search of the brand that is reading its question or collecting. */
	listRunning(): Observable<FindSearch[]>;
	/** Draft only: queues the brand's answer; the reply arrives through `watchSearch`. */
	sendMessage(searchId: string, text: string): Observable<FindSearch>;
	/** Draft only: accepting queues the fix like a message; declining settles at once. */
	answerSuggestion(searchId: string, accept: boolean): Observable<FindSearch>;
	openSavedSearch(savedId: string): Observable<FindSearch>;
	saveSearch(
		searchId: string,
		name: string | null,
		asNew: boolean
	): Observable<FindSearch>;
	renameSearch(searchId: string, name: string): Observable<FindSearch>;
	deleteSearch(searchId: string): Observable<void>;
	readMentions(
		searchId: string,
		request: FindReadMentionsRequest
	): Observable<SocialListeningMentionsResponse>;
	/** `range` is the page's date filter; every count takes the one the list was read with. */
	countNewMentions(
		searchId: string,
		range?: FindReadDates | null
	): Observable<FindNewMentionsCount>;
	countUnknownLocation(
		searchId: string,
		range?: FindReadDates | null
	): Observable<FindUnknownLocationCount>;
	/** `includeUnknownLocation` counts what "Show them" lists, so the chips agree with the feed. */
	countPlatforms(
		searchId: string,
		includeUnknownLocation: boolean,
		range?: FindReadDates | null
	): Observable<FindPlatformCounts>;
	/** The city catalogue, matched on `query`; `country` narrows it to one ISO code. */
	lookupCities(
		query: string,
		country: string | null
	): Observable<FindCityLookupItem[]>;
	/** The first-level division catalogue (states, provinces), matched like `lookupCities`. */
	lookupRegions(
		query: string,
		country: string | null
	): Observable<FindRegion[]>;
	exportPdf(searchId: string, mentionIds: string[]): Observable<Blob>;
	getExportView(
		searchId: string,
		exportToken: string
	): Observable<FindExportView>;
	createAccountListFromFile(file: File): Observable<FindAccountListJob>;
	createAccountListFromText(text: string): Observable<FindAccountListJob>;
	getAccountList(jobId: string): Observable<FindAccountListJob>;
	watchAccountList(jobId: string): Observable<FindAccountListJob>;
	getSuggestions(): Observable<FindSuggestions>;
}

/**
 * Re-reads a search until it settles: every 2 s while the question is being
 * interpreted or a draft is processing a message, every 5 s while collection
 * runs. Emits every status it reads and completes once none is in progress.
 */
export function pollFindSearch(
	read: () => Observable<FindSearch>
): Observable<FindSearch> {
	const nextDelay = (search: FindSearch): number | null => {
		if (search.status === 'interpreting' || search.status === 'drafting') {
			return FIND_INTERPRETING_POLL_MS;
		}
		return search.collection_status === 'collecting'
			? FIND_COLLECTING_POLL_MS
			: null;
	};
	return read().pipe(
		expand(search => {
			const delay = nextDelay(search);
			return delay === null
				? EMPTY
				: timer(delay).pipe(switchMap(() => read()));
		})
	);
}

/** Re-reads an account-list parse job until it completes or fails. */
export function pollFindAccountList(
	read: () => Observable<FindAccountListJob>
): Observable<FindAccountListJob> {
	return read().pipe(
		expand(job =>
			job.status === 'queued' || job.status === 'running'
				? timer(FIND_ACCOUNT_LIST_POLL_MS).pipe(switchMap(() => read()))
				: EMPTY
		)
	);
}

/** The filters as a PATCH carries them: the city and region names are the backend's to fill. */
function filtersForRequest(filters: FindFilters): FindFilters {
	const body = { ...filters };
	delete body.cities;
	delete body.regions;
	return body;
}

export function mapFindMention(
	mention: SocialListeningMentionApi
): SocialListeningMention {
	return {
		id: mention.id ?? '',
		influencerId: mention.influencer_id ?? undefined,
		authorName: mention.author_name,
		authorUsername: mention.author_username,
		authorImage: mention.author_image,
		authorVerified: mention.author_verified,
		authorUrl: mention.author_url,
		authorPlatform: normalizeSocialListeningPlatform(
			mention.author_platform
		),
		authorPlatformId: mention.author_platform_id ?? undefined,
		postUrl: mention.post_url ?? mention.author_url ?? '',
		postCaption: mention.post_caption,
		postImage: mention.post_image,
		postLikes: mention.post_likes,
		postComments: mention.post_comments,
		postViews: mention.post_views,
		postEngagementRate: mention.post_engagement_rate,
		postEstimatedReach: mention.post_estimated_reach,
		postDate: mention.post_date,
		sentiment: mention.sentiment as SocialListeningSentimentType,
		language: mention.language,
		// CONTRACT: a search owns no tags, so the chips would apply to nothing.
		tags: [],
		// CONTRACT: the backend fills this per item with the search's positive terms found in the post.
		matchedKeywords: mention.matched_keywords ?? [],
		isMessageable: mention.is_messageable ?? false,
		title: mention.title ?? '',
		domainVisits: mention.domain_visits ?? 0,
		origin: mention.origin ?? 'earned',
		parentUrl: mention.parent_url ?? '',
		ownAccount: mention.own_account ?? ''
	};
}

@Injectable({ providedIn: 'root' })
export class FindMentionsService implements IFindMentionsService {
	constructor(private http: HttpClient) {}

	// RAISES: 422 in Query mode without `draft` when the expression does not parse — body.error is the brand-readable reason.
	createSearch(request: FindCreateSearchRequest): Observable<FindSearch> {
		return this.http.post<FindSearch>(this.searchesUrl(), request);
	}

	listSearches(): Observable<FindSearchList> {
		return this.http.get<FindSearchList>(this.searchesUrl());
	}

	listHistory(request: FindHistoryRequest): Observable<FindHistoryPage> {
		let params = new HttpParams()
			.set('favorites', request.favorites ? 'true' : 'false')
			.set('running', request.running ? 'true' : 'false')
			.set('page', request.page)
			.set('page_size', request.page_size);
		if (request.q) params = params.set('q', request.q);
		return this.http.get<FindHistoryPage>(
			smiUrl()
				.add('social-listening')
				.add('find')
				.add('searches')
				.add('history')
				.done(),
			{ params }
		);
	}

	getSearch(searchId: string): Observable<FindSearch> {
		return this.http.get<FindSearch>(this.searchUrl(searchId).done());
	}

	watchSearch(searchId: string): Observable<FindSearch> {
		return pollFindSearch(() => this.getSearch(searchId));
	}

	getSearchAccountList(searchId: string): Observable<FindAccountListEntry[]> {
		return this.http
			.get<FindSearchAccountList>(
				this.searchUrl(searchId).add('account-list').done()
			)
			.pipe(map(response => response.entries));
	}

	// RAISES: 409 while the question is still being interpreted or the draft is processing a message, or the draft has no definition yet; 422 when no query term is left.
	updateSearch(
		searchId: string,
		request: FindUpdateSearchRequest
	): Observable<FindSearch> {
		return this.http.patch<FindSearch>(
			this.searchUrl(searchId).done(),
			request.filters
				? { ...request, filters: filtersForRequest(request.filters) }
				: request
		);
	}

	// RAISES: 422 for a draft that is not runnable; 409 for a draft still processing a message.
	runSearch(
		searchId: string,
		options?: FindRunOptions
	): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(searchId)
				.add('run')
				.query(
					'unless_stopped',
					options?.unlessStopped ? 'true' : undefined
				)
				.done(),
			null
		);
	}

	// RAISES: 409 for a draft — nothing is running. A run that already ended is answered unchanged, 200.
	stopSearch(
		searchId: string,
		request: FindStopRequest
	): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(searchId).add('stop').done(),
			request
		);
	}

	listRunning(): Observable<FindSearch[]> {
		return this.http
			.get<FindRunningList>(
				smiUrl()
					.add('social-listening')
					.add('find')
					.add('searches')
					.add('running')
					.done()
			)
			.pipe(map(response => response.searches));
	}

	// RAISES: 409 while the draft is busy; 422 once the draft's message limit is reached.
	sendMessage(searchId: string, text: string): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(searchId).add('messages').done(),
			{ text }
		);
	}

	// RAISES: 409 while the draft is busy.
	answerSuggestion(
		searchId: string,
		accept: boolean
	): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(searchId).add('suggestion').done(),
			{ accept }
		);
	}

	openSavedSearch(savedId: string): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(savedId).add('open').done(),
			null
		);
	}

	saveSearch(
		searchId: string,
		name: string | null,
		asNew: boolean
	): Observable<FindSearch> {
		return this.http.post<FindSearch>(
			this.searchUrl(searchId).add('save').done(),
			{ name, as_new: asNew }
		);
	}

	renameSearch(searchId: string, name: string): Observable<FindSearch> {
		return this.http.put<FindSearch>(
			this.searchUrl(searchId).add('name').done(),
			{ name }
		);
	}

	deleteSearch(searchId: string): Observable<void> {
		return this.http.delete<void>(this.searchUrl(searchId).done());
	}

	readMentions(
		searchId: string,
		request: FindReadMentionsRequest
	): Observable<SocialListeningMentionsResponse> {
		return this.http
			.post<SocialListeningMentionsResponseApi>(
				this.searchUrl(searchId).add('mentions').done(),
				request
			)
			.pipe(
				map(response => ({
					lastUpdateDate: response.last_update_date,
					mentions: response.mentions.map(mention =>
						mapFindMention(mention)
					),
					pagination: {
						page_number: response.pagination.page_number,
						total_pages: response.pagination.total_pages,
						records: response.pagination.records
					},
					mentionsLocked: response.mentions_locked === true
				}))
			);
	}

	countNewMentions(
		searchId: string,
		range?: FindReadDates | null
	): Observable<FindNewMentionsCount> {
		return this.http.get<FindNewMentionsCount>(
			this.searchUrl(searchId)
				.add('counts')
				.add('new')
				.query('date_from', range?.date_from)
				.query('date_to', range?.date_to)
				.done()
		);
	}

	countUnknownLocation(
		searchId: string,
		range?: FindReadDates | null
	): Observable<FindUnknownLocationCount> {
		return this.http.get<FindUnknownLocationCount>(
			this.searchUrl(searchId)
				.add('counts')
				.add('unknown-location')
				.query('date_from', range?.date_from)
				.query('date_to', range?.date_to)
				.done()
		);
	}

	countPlatforms(
		searchId: string,
		includeUnknownLocation: boolean,
		range?: FindReadDates | null
	): Observable<FindPlatformCounts> {
		return this.http.get<FindPlatformCounts>(
			this.searchUrl(searchId)
				.add('counts')
				.add('platforms')
				.query(
					'include_unknown_location',
					includeUnknownLocation ? 'true' : undefined
				)
				.query('date_from', range?.date_from)
				.query('date_to', range?.date_to)
				.done()
		);
	}

	lookupCities(
		query: string,
		country: string | null
	): Observable<FindCityLookupItem[]> {
		const url = smiUrl()
			.add('social-listening')
			.add('find')
			.add('cities')
			.query('q', encodeURIComponent(query))
			.query('country', country ?? undefined)
			.query('limit', FIND_CITY_LOOKUP_LIMIT)
			.done();
		return this.http.get<FindCityLookupItem[]>(url);
	}

	lookupRegions(
		query: string,
		country: string | null
	): Observable<FindRegion[]> {
		const url = smiUrl()
			.add('social-listening')
			.add('find')
			.add('regions')
			.query('q', encodeURIComponent(query))
			.query('country', country ?? undefined)
			.query('limit', FIND_CITY_LOOKUP_LIMIT)
			.done();
		return this.http.get<FindRegion[]>(url);
	}

	exportPdf(searchId: string, mentionIds: string[]): Observable<Blob> {
		return this.http.post(
			this.searchUrl(searchId).add('exports').add('pdf').done(),
			{ mention_ids: mentionIds },
			{ responseType: 'blob' }
		);
	}

	getExportView(
		searchId: string,
		exportToken: string
	): Observable<FindExportView> {
		const url = smiUrl()
			.add('social-listening')
			.add('find')
			.add('exports')
			.add(exportToken)
			.query('search_id', searchId)
			.done();
		// CONTRACT: the headless export page has no session; the token is its only credential.
		return this.http.get<FindExportView>(url, {
			context: new HttpContext().set(BYPASS_AUTH, true)
		});
	}

	createAccountListFromFile(file: File): Observable<FindAccountListJob> {
		const body = new FormData();
		body.append('file', file, file.name);
		return this.http.post<FindAccountListJob>(this.accountListsUrl(), body);
	}

	createAccountListFromText(text: string): Observable<FindAccountListJob> {
		return this.http.post<FindAccountListJob>(this.accountListsUrl(), {
			text
		});
	}

	getAccountList(jobId: string): Observable<FindAccountListJob> {
		return this.http.get<FindAccountListJob>(
			smiUrl()
				.add('social-listening')
				.add('find')
				.add('account-lists')
				.add(jobId)
				.done()
		);
	}

	watchAccountList(jobId: string): Observable<FindAccountListJob> {
		return pollFindAccountList(() => this.getAccountList(jobId));
	}

	getSuggestions(): Observable<FindSuggestions> {
		return this.http.get<FindSuggestions>(
			smiUrl()
				.add('social-listening')
				.add('find')
				.add('suggestions')
				.done()
		);
	}

	private searchesUrl(): string {
		return smiUrl()
			.add('social-listening')
			.add('find')
			.add('searches')
			.done();
	}

	private searchUrl(searchId: string) {
		return smiUrl()
			.add('social-listening')
			.add('find')
			.add('searches')
			.add(searchId);
	}

	private accountListsUrl(): string {
		return smiUrl()
			.add('social-listening')
			.add('find')
			.add('account-lists')
			.done();
	}
}
